import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Button, Flex, Table, Spin, Tooltip, message } from 'antd'
import type { TableProps } from 'antd'
import { Link, useNavigate } from 'react-router-dom'
import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { SearchInput } from '@/components/SearchInput'
import Title from 'antd/es/typography/Title'
import { type Permission } from '@/data'
import { getPermissions } from '@/api/permissions/getPermissions'
import { deletePermission } from '@/api/permissions/deletePermission'
import { AuthContext } from '@/contexts/AuthContext'
import { hasPermission } from '@/auth/access'
import { getErrorMessage } from '@/utils/getErrorMessage'

export const AdminPermissionsPage: React.FC = () => {
  const navigate = useNavigate()
  const auth = useContext(AuthContext)!
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  })
  const [search, setSearch] = useState('')

  const hasFetched = useRef(false)
  const canReadPermissions =
    hasPermission(auth.user, 'lop:ui:read:create:permissions') ||
    hasPermission(auth.user, 'lop:ui:path:read:/admin/permissions')
  const canCreatePermissions = hasPermission(
    auth.user,
    'lop:ui:action:create:permissions',
  )
  const canUpdatePermissions = hasPermission(
    auth.user,
    'lop:ui:action:update:permissions',
  )
  const canDeletePermissions = hasPermission(
    auth.user,
    'lop:ui:action:delete:permissions',
  )

  const fetchPermissions = useCallback(
    async (page = 1, query = search) => {
      try {
        setLoading(true)
        setError(null)
        const { data, meta } = await getPermissions({
          page,
          ...(query ? { filters: [{ key: 'name', value: query }] } : {}),
        })
        setPermissions(data)
        setPagination({
          current: meta.current_page,
          pageSize: meta.per_page,
          total: meta.total,
        })
      } catch (err) {
        setError('Failed to load permissions')
        console.error(err)
      } finally {
        setLoading(false)
      }
    },
    [search],
  )

  const handleSearch = useCallback(
    (value: string) => {
      setSearch(value)
      fetchPermissions(1, value)
    },
    [fetchPermissions],
  )

  useEffect(() => {
    if (!canReadPermissions) {
      setLoading(false)
      return
    }

    if (hasFetched.current) return
    hasFetched.current = true
    fetchPermissions()
  }, [canReadPermissions, fetchPermissions])

  const handleDelete = async (id: number) => {
    try {
      await deletePermission(String(id))
      message.success('Permission deleted')
      fetchPermissions(pagination.current)
    } catch (err) {
      message.error(getErrorMessage(err, 'Failed to delete permission'))
      console.error(err)
    }
  }

  const columns: TableProps<Permission>['columns'] = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) =>
        canUpdatePermissions ? (
          <Link to={`/admin/permissions/${record.id}/edit`}>{text}</Link>
        ) : (
          text
        ),
    },
    {
      title: 'Guard',
      dataIndex: 'guard_name',
      key: 'guard_name',
      sorter: (a, b) => a.guard_name.localeCompare(b.guard_name),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <>
          {canUpdatePermissions ? (
            <Tooltip title="Edit">
              <Link to={`/admin/permissions/${record.id}/edit`}>
                <EditOutlined style={{ fontSize: 16, marginRight: 12 }} />
              </Link>
            </Tooltip>
          ) : null}
          {canDeletePermissions ? (
            <Tooltip title="Delete">
              <DeleteOutlined
                style={{ fontSize: 16, color: '#ff4d4f' }}
                onClick={() => handleDelete(record.id)}
              />
            </Tooltip>
          ) : null}
        </>
      ),
    },
  ]

  if (error) {
    return (
      <div>
        <Title level={4}>Permissions</Title>
        <p style={{ color: '#ff4d4f' }}>{error}</p>
        <Button onClick={() => fetchPermissions()}>Retry</Button>
      </div>
    )
  }

  return (
    <div>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Permissions
        </Title>
        <Flex gap="small" align="center">
          <SearchInput onSearch={handleSearch} />
          {canCreatePermissions ? (
            <Button
              type="primary"
              onClick={() => navigate('/admin/permissions/new')}
            >
              New
            </Button>
          ) : null}
        </Flex>
      </Flex>
      <Spin spinning={loading}>
        <Table<Permission>
          rowKey="id"
          columns={columns}
          dataSource={permissions}
          pagination={pagination}
          onChange={(p) => fetchPermissions(p.current, search)}
        />
      </Spin>
    </div>
  )
}
